import axios from 'axios'
import { PUBLIC_ROUTES } from '../routes'
import { buildWidgetRedirectFromLocation } from './widgetRedirect'
import {
    clearWidgetSession,
    getWidgetCodeFromLocation,
    getWidgetToken,
    isActiveProjectWidgetMode,
    isWidgetTokenInvalidResponse,
    refreshWidgetTokenFromApi,
    resolveWidgetCodeForRoute,
} from './widgetAuth'

export const getToken = () =>
    localStorage.getItem('authToken')
        ? JSON.parse(localStorage.getItem('authToken'))
        : null

export const getAuthorizationHeader = () => `Bearer ${getToken()}`
export const getBackendBaseUrl = () =>
    window.location.hostname === 'localhost'
        ? `${process.env.REACT_APP_URL}/backend`
        : 'https://pmx.softwareco.com/backend'

axios.defaults.withCredentials = true

const axiosNew = axios.create({
    baseURL: getBackendBaseUrl(),
    headers: {
        'Content-Type': 'application/json',
    },
    method: 'post',
})

axiosNew.interceptors.request.use((config) => {
    if (isActiveProjectWidgetMode()) {
        const widgetToken = getWidgetToken()
        if (widgetToken) {
            config.headers = config.headers ?? {}
            config.headers.Authorization = `Bearer ${widgetToken}`
        }
        return config
    }

    const userToken = getToken()
    if (userToken) {
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${userToken}`
    }
    return config
})

const PUBLIC_ROUTE_PATHS = Object.values(PUBLIC_ROUTES).filter(
    (route) => typeof route === 'string'
)

const isPublicRoutePath = (pathname) =>
    Boolean(pathname) && PUBLIC_ROUTE_PATHS.includes(pathname)

const handleUnauthenticated = () => {
    if (isActiveProjectWidgetMode()) {
        return
    }

    try {
        localStorage.removeItem('authToken')
        localStorage.removeItem('currentProject')
    } catch {
        // ignore
    }

    try {
        const pathname = window.location?.pathname
        if (!isPublicRoutePath(pathname)) {
            const widgetRedirect = buildWidgetRedirectFromLocation(
                window.location.pathname,
                window.location.search
            )
            if (widgetRedirect) {
                sessionStorage.setItem(
                    'pendingWidgetRedirect',
                    JSON.stringify(widgetRedirect)
                )
            }
            window.location.assign(PUBLIC_ROUTES.login)
        }
    } catch {
        // ignore
    }
}

const retryWithRefreshedWidgetToken = async (originalRequest) => {
    const routeSegment = getWidgetCodeFromLocation()
    const widgetCode = resolveWidgetCodeForRoute(
        routeSegment,
        window.location.search
    )
    if (!widgetCode) {
        return Promise.reject(new Error('Widget code missing'))
    }

    await refreshWidgetTokenFromApi(widgetCode, { force: true })
    const token = getWidgetToken()
    if (!token) {
        return Promise.reject(new Error('Widget token refresh failed'))
    }

    originalRequest._widgetRetry = true
    originalRequest.headers = originalRequest.headers ?? {}
    originalRequest.headers.Authorization = `Bearer ${token}`
    return axiosNew(originalRequest)
}

const handleWidgetTokenInvalid = async (error) => {
    const originalRequest = error?.config
    if (
        !originalRequest ||
        originalRequest._widgetRetry ||
        !isActiveProjectWidgetMode()
    ) {
        return Promise.reject(error)
    }

    try {
        return await retryWithRefreshedWidgetToken(originalRequest)
    } catch (refreshError) {
        clearWidgetSession()
        return Promise.reject(refreshError)
    }
}

axiosNew.interceptors.response.use(
    (response) => {
        if (
            isActiveProjectWidgetMode() &&
            isWidgetTokenInvalidResponse(response)
        ) {
            return handleWidgetTokenInvalid({
                response,
                config: response.config,
            })
        }

        const code = response?.data?.code
        if (code === 'UNAUTHENTICATED') {
            handleUnauthenticated()
        }
        return response
    },
    (error) => {
        if (isActiveProjectWidgetMode() && isWidgetTokenInvalidResponse(error)) {
            return handleWidgetTokenInvalid(error)
        }

        const code = error?.response?.data?.code
        if (code === 'UNAUTHENTICATED') {
            handleUnauthenticated()
        }
        return Promise.reject(error)
    }
)

export default axiosNew
